import { useEffect, useState } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { History, ChevronDown, ChevronUp, EyeOff } from 'lucide-react';

const when = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return isNaN(d) ? value : d.toLocaleString();
};

/** The audit trail for one incident report: who changed what, and when. */
export const IncidentReportHistory = ({ reportId }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || data || loading) return;
    setLoading(true);
    axiosInstance
      .get(`/incident-reports/${reportId}/history`)
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.data?.detail || 'Could not load the history'))
      .finally(() => setLoading(false));
  }, [open, reportId, data, loading]);

  return (
    <div className="mt-3 border-t pt-3" data-testid={`incident-history-${reportId}`}>
      <Button
        size="sm"
        variant="ghost"
        className="text-gray-600 hover:text-gray-900 px-2"
        onClick={() => setOpen(!open)}
        data-testid={`incident-history-toggle-${reportId}`}
      >
        <History className="w-4 h-4 mr-2" />
        Change history
        {open ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
      </Button>

      {open && (
        <div className="mt-2 pl-2">
          {loading && <p className="text-sm text-gray-500">Loading…</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}

          {data && (
            <>
              {data.removedFromSupervisorView && (
                <div
                  className="flex items-center text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2"
                  data-testid={`incident-removed-from-supervisor-${reportId}`}
                >
                  <EyeOff className="w-3 h-3 mr-2" />
                  The submitting supervisor cleared this from their page. This record is retained.
                </div>
              )}

              {data.entries.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No changes since it was filed{data.createdAt ? ` on ${when(data.createdAt)}` : ''}.
                </p>
              ) : (
                <ol className="space-y-3">
                  {data.entries.map((entry) => (
                    <li key={entry.id} className="text-sm" data-testid={`history-entry-${entry.id}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{entry.changedByName || 'Unknown user'}</span>
                        <Badge variant="outline" className="text-xs">
                          {(entry.changedByRole || '').replace(/_/g, ' ')}
                        </Badge>
                        <span className="text-gray-500 text-xs">{when(entry.changedAt)}</span>
                      </div>
                      {entry.note && <p className="text-gray-600 text-xs mt-1">{entry.note}</p>}
                      {entry.changes?.length > 0 && (
                        <ul className="mt-1 space-y-0.5">
                          {entry.changes.map((change) => (
                            <li key={change.field} className="text-xs text-gray-700">
                              <span className="font-mono">{change.field}</span>:{' '}
                              <span className="line-through text-gray-400">
                                {change.from || '(empty)'}
                              </span>{' '}
                              → <span className="text-gray-900">{change.to || '(empty)'}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default IncidentReportHistory;
